import React, { useState, useEffect } from 'react';
import { availabilityAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function Availability() {
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const availRes = await availabilityAPI.get();
      const availMap = {};
      availRes.data.forEach(a => {
        availMap[a.day_of_week] = a;
      });
      const fullAvailability = dayNames.map((_, index) => {
        if (availMap[index]) {
          return availMap[index];
        }
        return {
          day_of_week: index,
          start_time: '09:00',
          end_time: '17:00',
          is_available: true,
        };
      });
      setAvailability(fullAvailability);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const handleAvailabilityChange = async (dayIndex, field, value) => {
    const updated = [...availability];
    updated[dayIndex] = { ...updated[dayIndex], [field]: value };
    setAvailability(updated);

    try {
      await availabilityAPI.set({
        day_of_week: dayIndex,
        start_time: updated[dayIndex].start_time,
        end_time: updated[dayIndex].end_time,
        is_available: updated[dayIndex].is_available,
      });
    } catch (error) {
      toast.error('Failed to save availability');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="availability-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Availability</h1>
          <p className="text-muted-foreground mt-1">Manage your weekly work schedule</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            Your Weekly Availability
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {availability.map((day, index) => (
              <div
                key={dayNames[index]}
                className={`flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-lg border ${
                  day.is_available ? 'bg-background' : 'bg-muted/50'
                }`}
                data-testid={`availability-day-${index}`}
              >
                <div className="flex items-center justify-between md:w-32">
                  <span className="font-medium">{dayNames[index]}</span>
                  <Switch
                    checked={day.is_available}
                    onCheckedChange={(checked) => handleAvailabilityChange(index, 'is_available', checked)}
                    data-testid={`availability-switch-${index}`}
                  />
                </div>

                {day.is_available && (
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">From</Label>
                      <Input
                        type="time"
                        value={day.start_time}
                        onChange={(e) => handleAvailabilityChange(index, 'start_time', e.target.value)}
                        className="w-32"
                        data-testid={`availability-start-${index}`}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">To</Label>
                      <Input
                        type="time"
                        value={day.end_time}
                        onChange={(e) => handleAvailabilityChange(index, 'end_time', e.target.value)}
                        className="w-32"
                        data-testid={`availability-end-${index}`}
                      />
                    </div>
                  </div>
                )}

                {!day.is_available && (
                  <span className="text-sm text-muted-foreground">Not available</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
